import { supabase } from "@/integrations/supabase/client";

export interface PreviewData {
  candidate?: any;
  job?: any;
  company?: any;
  contact?: any;
  match?: any;
  user?: any;
  system?: any;
}

export const TemplatePreviewService = {
  async fetchPreviewData(matchId?: string): Promise<PreviewData> {
    try {
      const previewData: PreviewData = {};

      // Get current user info
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name, email')
          .eq('id', user.id)
          .single();

        if (profile) {
          previewData.user = {
            first_name: profile.first_name || 'John',
            last_name: profile.last_name || 'Smith',
            email: profile.email || 'john.smith@company.com'
          };
        }
      }

      // Get organization info for system data
      const { data: userOrg } = await supabase
        .from('user_organization')
        .select('organization_uuid, organizations(name)')
        .eq('user_uuid', user?.id)
        .single();

      previewData.system = {
        current_date: new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        current_time: new Date().toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        organization_name: userOrg?.organizations?.name || 'Your Organization'
      };

      // If matchId is provided, fetch specific match data
      if (matchId) {
        const { data: matchData } = await supabase
          .from('matches')
          .select(`
            *,
            candidates(*),
            jobs(*, companies(*)),
            jobs(contacts(*))
          `)
          .eq('id', matchId)
          .single();

        if (matchData) {
          // Use specific match data
          const candidate = matchData.candidates;
          const job = matchData.jobs;
          const company = job?.companies;
          const contact = job?.contacts;

          if (candidate) {
            previewData.candidate = {
              name: candidate.name || 'John Doe',
              email: candidate.email || 'john.doe@example.com',
              phone: candidate.phone || '+1 234 567 8900',
              function: candidate.function || 'Software Engineer',
              location: candidate.location || 'New York, NY',
              linkedin: candidate.linkedin || 'https://linkedin.com/in/johndoe',
              status: candidate.status || 'Active',
              manager: candidate.manager || 'Jane Smith',
              created_at: candidate.created_at ? new Date(candidate.created_at).toLocaleDateString() : '2024-01-15'
            };
          }

          if (job) {
            previewData.job = {
              title: job.title || 'Senior Software Engineer',
              description: job.description || 'We are looking for a talented software engineer...',
              employment_type: job.employment_type || 'Full-time',
              location_type: job.location_type || 'Remote',
              seniority_level: job.seniority_level || 'Senior',
              budget: job.budget || '$80,000 - $120,000',
              start_date: job.start_date || '2024-03-01',
              application_deadline: job.application_deadline || '2024-02-15',
              assigned_recruiter: job.assigned_recruiter || 'Sarah Johnson',
              status: job.status || 'open'
            };
          }

          if (company) {
            previewData.company = {
              name: company.name || 'Tech Solutions Inc.',
              address: company.address || '123 Main St, San Francisco, CA 94105',
              street: company.street || '123 Main St',
              city: company.city || 'San Francisco',
              country: company.country || 'United States',
              website_url: company.website_url || 'https://techsolutions.com',
              assignee: company.assignee || 'Mike Wilson',
              vat_number: company.vat_number || 'US123456789'
            };
          }

          if (contact) {
            previewData.contact = {
              name: contact.name || 'Alice Brown',
              first_name: contact.first_name || 'Alice',
              last_name: contact.last_name || 'Brown',
              email: contact.email || 'alice.brown@company.com',
              phone: contact.phone || '+1 555 123 4567',
              function: contact.function || 'HR Manager'
            };
          }

          // Add match-specific data
          previewData.match = {
            score: `${matchData.score || 85}%`,
            description: matchData.description || 'Strong technical fit with relevant experience',
            status: matchData.status || 'Under Review',
            created_at: matchData.created_at ? new Date(matchData.created_at).toLocaleDateString() : '2024-01-20'
          };
        }
      }

      // If no match data or fallback needed, fetch sample data
      if (!previewData.candidate) {
        const { data: candidates } = await supabase
          .from('candidates')
          .select('*')
          .limit(1);

        if (candidates && candidates.length > 0) {
          const candidate = candidates[0];
          previewData.candidate = {
            name: candidate.name || 'John Doe',
            email: candidate.email || 'john.doe@example.com',
            phone: candidate.phone || '+1 234 567 8900',
            function: candidate.function || 'Software Engineer',
            location: candidate.location || 'New York, NY',
            linkedin: candidate.linkedin || 'https://linkedin.com/in/johndoe',
            status: candidate.status || 'Active',
            manager: candidate.manager || 'Jane Smith',
            created_at: candidate.created_at ? new Date(candidate.created_at).toLocaleDateString() : '2024-01-15'
          };
        } else {
          // Fallback sample data if no candidates exist
          previewData.candidate = {
            name: 'John Doe',
            email: 'john.doe@example.com',
            phone: '+1 234 567 8900',
            function: 'Software Engineer',
            location: 'New York, NY',
            linkedin: 'https://linkedin.com/in/johndoe',
            status: 'Active',
            manager: 'Jane Smith',
            created_at: '2024-01-15'
          };
        }
      }

      // Fetch sample job data if not already set
      if (!previewData.job) {
        const { data: jobs } = await supabase
          .from('jobs')
          .select(`
            *,
            companies(name, website_url, city, country)
          `)
          .limit(1);

        if (jobs && jobs.length > 0) {
          const job = jobs[0];
          previewData.job = {
            title: job.title || 'Senior Software Engineer',
            description: job.description || 'We are looking for a talented software engineer...',
            employment_type: job.employment_type || 'Full-time',
            location_type: job.location_type || 'Remote',
            seniority_level: job.seniority_level || 'Senior',
            budget: job.budget || '$80,000 - $120,000',
            start_date: job.start_date || '2024-03-01',
            application_deadline: job.application_deadline || '2024-02-15',
            assigned_recruiter: job.assigned_recruiter || 'Sarah Johnson',
            status: job.status || 'open'
          };

          if (job.companies && !previewData.company) {
            previewData.company = {
              name: job.companies.name || 'Tech Solutions Inc.',
              website_url: job.companies.website_url || 'https://techsolutions.com',
              city: job.companies.city || 'San Francisco',
              country: job.companies.country || 'United States'
            };
          }
        } else {
          // Fallback sample data
          previewData.job = {
            title: 'Senior Software Engineer',
            description: 'We are looking for a talented software engineer to join our growing team...',
            employment_type: 'Full-time',
            location_type: 'Remote',
            seniority_level: 'Senior',
            budget: '$80,000 - $120,000',
            start_date: '2024-03-01',
            application_deadline: '2024-02-15',
            assigned_recruiter: 'Sarah Johnson',
            status: 'open'
          };

          if (!previewData.company) {
            previewData.company = {
              name: 'Tech Solutions Inc.',
              website_url: 'https://techsolutions.com',
              city: 'San Francisco',
              country: 'United States'
            };
          }
        }
      }

      // Fetch sample contact data
      const { data: contacts } = await supabase
        .from('contacts')
        .select('*')
        .limit(1);

      if (contacts && contacts.length > 0) {
        const contact = contacts[0];
        previewData.contact = {
          name: contact.name || 'Alice Brown',
          first_name: contact.first_name || 'Alice',
          last_name: contact.last_name || 'Brown',
          email: contact.email || 'alice.brown@company.com',
          phone: contact.phone || '+1 555 123 4567',
          function: contact.function || 'HR Manager'
        };
      } else {
        previewData.contact = {
          name: 'Alice Brown',
          first_name: 'Alice',
          last_name: 'Brown',
          email: 'alice.brown@company.com',
          phone: '+1 555 123 4567',
          function: 'HR Manager'
        };
      }

      // Fetch sample match data
      const { data: matches } = await supabase
        .from('matches')
        .select('*')
        .limit(1);

      if (matches && matches.length > 0) {
        const match = matches[0];
        previewData.match = {
          score: `${match.score || 85}%`,
          description: match.description || 'Strong technical fit with relevant experience',
          status: match.status || 'Under Review',
          created_at: match.created_at ? new Date(match.created_at).toLocaleDateString() : '2024-01-20'
        };
      } else {
        previewData.match = {
          score: '85%',
          description: 'Strong technical fit with relevant experience',
          status: 'Under Review',
          created_at: '2024-01-20'
        };
      }

      // Add remaining company data if not already set
      if (!previewData.company) {
        const { data: companies } = await supabase
          .from('companies')
          .select('*')
          .limit(1);

        if (companies && companies.length > 0) {
          const company = companies[0];
          previewData.company = {
            name: company.name || 'Tech Solutions Inc.',
            address: company.address || '123 Main St, City, State 12345',
            street: company.street || '123 Main St',
            city: company.city || 'San Francisco',
            country: company.country || 'United States',
            website_url: company.website_url || 'https://techsolutions.com',
            assignee: company.assignee || 'Mike Wilson',
            vat_number: company.vat_number || 'US123456789'
          };
        } else {
          previewData.company = {
            name: 'Tech Solutions Inc.',
            address: '123 Main St, San Francisco, CA 94105',
            street: '123 Main St',
            city: 'San Francisco',
            country: 'United States',
            website_url: 'https://techsolutions.com',
            assignee: 'Mike Wilson',
            vat_number: 'US123456789'
          };
        }
      }

      return previewData;
    } catch (error) {
      console.error('Error fetching preview data:', error);
      
      // Return fallback data if database fetch fails
      return {
        candidate: {
          name: 'John Doe',
          email: 'john.doe@example.com',
          phone: '+1 234 567 8900',
          function: 'Software Engineer',
          location: 'New York, NY',
          linkedin: 'https://linkedin.com/in/johndoe',
          status: 'Active',
          manager: 'Jane Smith',
          created_at: '2024-01-15'
        },
        job: {
          title: 'Senior Software Engineer',
          description: 'We are looking for a talented software engineer...',
          employment_type: 'Full-time',
          location_type: 'Remote',
          seniority_level: 'Senior',
          budget: '$80,000 - $120,000',
          start_date: '2024-03-01',
          application_deadline: '2024-02-15',
          assigned_recruiter: 'Sarah Johnson',
          status: 'open'
        },
        company: {
          name: 'Tech Solutions Inc.',
          address: '123 Main St, San Francisco, CA 94105',
          street: '123 Main St',
          city: 'San Francisco',
          country: 'United States',
          website_url: 'https://techsolutions.com',
          assignee: 'Mike Wilson',
          vat_number: 'US123456789'
        },
        contact: {
          name: 'Alice Brown',
          first_name: 'Alice',
          last_name: 'Brown',
          email: 'alice.brown@company.com',
          phone: '+1 555 123 4567',
          function: 'HR Manager'
        },
        match: {
          score: '85%',
          description: 'Strong technical fit with relevant experience',
          status: 'Under Review',
          created_at: '2024-01-20'
        },
        user: {
          first_name: 'John',
          last_name: 'Smith',
          email: 'john.smith@company.com'
        },
        system: {
          current_date: new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          current_time: new Date().toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          organization_name: 'Your Organization'
        }
      };
    }
  },

  replaceTemplateVariables(content: string, data: PreviewData): string {
    let processedContent = content;

    // Replace all template variables with actual data
    Object.entries(data).forEach(([entityType, entityData]) => {
      if (entityData && typeof entityData === 'object') {
        Object.entries(entityData).forEach(([field, value]) => {
          const placeholder = `{{${entityType}.${field}}}`;
          const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
          processedContent = processedContent.replace(regex, String(value || ''));
        });
      }
    });

    return processedContent;
  },
};
